/**
 * Deployment detail for Apprise on an Azure VM:
 *  - the two INDEPENDENT tracks (Infrastructure and Application),
 *  - the Terraform plan review & approval panel (the shared plan-approval panel),
 *  - the server outputs (no secrets) and the control-plane verification block.
 *
 * Nothing here claims Apprise is installed: the application track reports
 * "not configured" and a deployment ends in "Infrastructure Ready".
 */
import { Fragment, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { ExternalLink, ShieldCheck } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader } from '@/components/common/Card'
import { PlanApprovalPanel } from '@/components/plan-approval/PlanApprovalPanel'
import { deploymentKeys, useAppriseVmDetail } from '@/hooks/useDeployments'
import { useAppriseVmOptions } from '@/hooks/useProducts'
import { formatDateTime } from '@/utils/format'
import type { DeploymentStatus, DeploymentTracks } from '@/types'
import { AppriseReviewGroups } from './AppriseReviewSection'
import { normalizeAppriseConfig } from './appriseReview'
import {
  APPRISE_PLAN_COPY, APP_TRACK_LABELS, APP_TRACK_TONE, INFRA_TRACK_LABELS, INFRA_TRACK_TONE, VERIFY_HEADING, appMessage,
  appriseConfirmCreateText, resolveTracks, serverOutputRows, shouldPollAppriseDetail, verifyRows,
} from './appriseDetail'
import styles from './AppriseDeployment.module.css'

function TracksCard({ tracks }: { tracks: DeploymentTracks }) {
  const infra = tracks.infra
  const app = tracks.app
  return (
    <Card>
      <CardHeader title="Deployment tracks" subtitle="Infrastructure and application progress are tracked independently" />
      <CardContent>
        <div className={styles.tracks}>
          <div className={styles.track} data-testid="infra-track">
            <span className={styles.trackTitle}>Infrastructure track</span>
            <span className={clsx(styles.pill, styles[INFRA_TRACK_TONE[infra.status] ?? 'muted'])}>
              {INFRA_TRACK_LABELS[infra.status] ?? infra.status}
            </span>
            {infra.message && <span className={styles.trackMessage}>{infra.message}</span>}
            {infra.run_url && (
              <a className={styles.runLink} href={infra.run_url} target="_blank" rel="noopener noreferrer">
                GitHub Actions run <ExternalLink size={11} />
              </a>
            )}
          </div>
          <div className={styles.track} data-testid="app-track">
            <span className={styles.trackTitle}>Application track</span>
            <span className={clsx(styles.pill, styles[APP_TRACK_TONE[app.status] ?? 'muted'])}>
              {APP_TRACK_LABELS[app.status] ?? app.status}
            </span>
            <span className={styles.trackMessage}>{appMessage(tracks)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface Props {
  deploymentId: number
  status: DeploymentStatus
  /** Tracks from the deployment payload — used until the detail endpoint answers. */
  deploymentTracks?: DeploymentTracks | null
}

export function AppriseDeploymentPanel({ deploymentId, status, deploymentTracks }: Props) {
  const queryClient = useQueryClient()
  const query = useAppriseVmDetail(deploymentId, true, shouldPollAppriseDetail(status))
  const { data: options } = useAppriseVmOptions(true)
  const { data } = query

  // Polling stops at infra_ready: refetch once on every status change so the final
  // outputs / verify block are picked up.
  const lastStatus = useRef(status)
  useEffect(() => {
    if (lastStatus.current === status) return
    lastStatus.current = status
    void queryClient.invalidateQueries({ queryKey: deploymentKeys.appriseVm(deploymentId) })
  }, [status, deploymentId, queryClient])

  const tracks = resolveTracks(data?.tracks, deploymentTracks, status)
  const outputs = serverOutputRows(data?.outputs)
  const verify = verifyRows(data?.verify)

  return (
    <>
      <TracksCard tracks={tracks} />

      <PlanApprovalPanel
        deploymentId={deploymentId}
        status={status}
        query={query}
        title="Apprise VM — Terraform plan"
        loadErrorText="Could not load the Apprise VM plan details."
        confirmApproveText={appriseConfirmCreateText}
        copy={APPRISE_PLAN_COPY}
        renderConfig={(config) => <AppriseReviewGroups config={normalizeAppriseConfig(config)} options={options} />}
        showOutputs={false}
      />

      <Card>
        <CardHeader title="Server" subtitle="Resolved after the plan is applied — no secret values are ever shown" />
        <CardContent>
          {outputs.length === 0 ? (
            <span className={styles.empty}>The server has not been created yet.</span>
          ) : (
            <dl className={styles.kv} data-testid="server-outputs">
              {outputs.map((r) => (
                <Fragment key={r.label}>
                  <dt>{r.label}</dt>
                  <dd className={r.mono ? styles.mono : undefined}>{r.value}</dd>
                </Fragment>
              ))}
            </dl>
          )}

          {verify.length > 0 && (
            <div className={styles.verified} data-testid="verify-block">
              <div className={styles.verifiedTitle}><ShieldCheck size={14} />{VERIFY_HEADING}</div>
              <dl className={styles.kv}>
                {verify.map((r) => (
                  <Fragment key={r.label}><dt>{r.label}</dt><dd>{r.value}</dd></Fragment>
                ))}
                {data?.verify?.checked_at && <><dt>Checked at</dt><dd>{formatDateTime(data.verify.checked_at)}</dd></>}
              </dl>
              <div className={styles.note}>
                This confirms the VM exists and is running according to Azure. It does not test network reachability and
                does not mean Apprise is installed.
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
